import { useEffect, useState, useMemo } from "react";
import { SidebarRight, confirm } from "@tw/tw-common-js";
import api from "../services/api";
import { Table } from "@tw/tw-common-js";
import { useVisibility } from "../context/useVisibility";
import { useDictionary } from "../context/useDictionary";
import EmployeViewModal from "../components/employee/EmployeViewModal";

export default function EmployeList() {
  const VISIBILITY_KEY = "employee";
  const { searchMap, setSearchField } = useVisibility();
  const visibility = useVisibility();
  const { getDictionaryValue, dictionaryMap } = useDictionary();
  const [rawList, setRawList] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Get Data
  useEffect(() => {
    setLoading(true);
    api.getEmployees().then((data) => {
      if (data.status !== 200) {
        confirm({ title: "Error", message: "Error fetching employees" });
        setRawList([]);
      } else {
        setRawList(data.data);
      }
    });
  }, []);

  useEffect(() => {
    setLoading(!(rawList.length > 0 && Object.keys(dictionaryMap).length > 0));
  }, [rawList, dictionaryMap]);

  // Populate list with dictionaries
  const mappedList = useMemo(() => {
    return rawList.map((item) => ({
      ...item,
      position: getDictionaryValue("Positions", item._position) || "",
      twOffice: getDictionaryValue("TW Offices", item._twOffice) || "",
    }));
  }, [rawList, getDictionaryValue]);

  // Filter List
  const filteredList = useMemo(() => {
    let filter = searchMap[VISIBILITY_KEY].filter;
    let searchText = searchMap[VISIBILITY_KEY].searchText;

    return mappedList.filter((item) => {
      if (filter.isArchived !== undefined && item.isArchived !== filter.isArchived) return false;
      if (filter.isPreHired === false && item.isPreHired === true) return false;
      if (filter.firstName && !item.firstName?.toLowerCase().includes(filter.firstName.toLowerCase())) return false;
      if (filter.lastName && !item.lastName?.toLowerCase().includes(filter.lastName.toLowerCase())) return false;
      if (filter.email && !item.email?.toLowerCase().includes(filter.email.toLowerCase())) return false;
      if (filter.hrCode && !item.hrCode?.toLowerCase().includes(filter.hrCode.toLowerCase())) return false;
      if (filter.position && !item.position?.toLowerCase().includes(filter.position.toLowerCase())) return false;
      if (filter.team && !item.Team?.name?.toLowerCase().includes(filter.team.toLowerCase())) return false;
      if (filter.office && !item.twOffice?.toLowerCase().includes(filter.office.toLowerCase())) return false;

      if (searchText) {
        const search = searchText.toLowerCase();
        const fields = [item.firstName, item.lastName, item.email, item.hrCode, item.position, item.Team?.name, item.twOffice];
        if (!fields.some((f) => f && f.toLowerCase().includes(search))) return false;
      }
      return true;
    });
  }, [mappedList, searchMap]);

  // Table config
  const config = {
    onClickRow: (item) => setSelectedEmployee(item),
    columns: [
      { header: "HR Code", key: "hrCode" },
      { header: "First Name", key: "firstName" },
      { header: "Last Name", key: "lastName" },
      { header: "Email", key: "email" },
      { header: "Position", key: "position" },
      { header: "Office", key: "twOffice" },
      { header: "Team", key: "Team.name", template: (item) => item.Team?.name || "" },
      { header: "Department", key: "Team.Department.name", template: (item) => item.Team?.Department?.name || "" },
      {
        header: "Division",
        key: "Team.Department.Division.name",
        template: (item) => item.Team?.Department?.Division?.name || "",
      },
      { header: "Country", key: "country" },
    ],
  };

  // Change filter utils fn
  const setFilterValue = (key, value) => {
    let prev = searchMap[VISIBILITY_KEY].filter;
    prev[key] = value;
    setSearchField(VISIBILITY_KEY, "filter", prev);
  };

  return (
    <>
      {selectedEmployee && <EmployeViewModal employee={selectedEmployee} onClose={() => setSelectedEmployee(null)} />}
      <div className={"page-wrapper" + (showSidebar ? " show-sidebar-right" : "")}>
        <div className="btn-bar">
          <div className="btn-bar-left">
            <div className="title">Teammates</div>
          </div>
          <div className="btn-bar-right">
            <div className="searchbar-container">
              <input
                type="text"
                className="searchbar-input"
                placeholder="Search"
                value={searchMap[VISIBILITY_KEY].searchText}
                onChange={(e) => setSearchField(VISIBILITY_KEY, "searchText", e.target.value)}
              />
              {searchMap[VISIBILITY_KEY].searchText && (
                <span className="searchbar-clear" onClick={() => setSearchField(VISIBILITY_KEY, "searchText", "")} title="Clear">
                  ×
                </span>
              )}
              <span className="searchbar-icon">
                <i className="material-icons">search</i>
              </span>
            </div>
            {!showSidebar && (
              <button className="blue-light" onClick={() => setShowSidebar(true)}>
                <span className="material-symbols-outlined">filter_alt</span>
                <div>Filters</div>
              </button>
            )}
          </div>
        </div>
        <div className="table-container">
          <Table
            config={config}
            // filter={searchMap[VISIBILITY_KEY].filter}
            data={filteredList}
            loading={loading}
            rowClassName={(row) => (row.isArchived ? "archived-row" : "")}
            enableColumnOrdering={true}
            rowIcon="person"
            showFooter={true}
            paginationCountText="TOTAL TEAMMATES: "
            visibilityKey={VISIBILITY_KEY}
            visibilityContext={visibility}
          />
        </div>
      </div>
      <SidebarRight show={showSidebar}>
        <div className="sidebar-header">
          <div className="sidebar-title">Filters</div>
          <div className="sidebar-title-right">
            <button className="medium icon-right" onClick={() => setShowSidebar(false)}>
              Close
              <i className="material-icons">chevron_right</i>
            </button>
          </div>
        </div>
        <div className="filter-container">
          {["hrCode", "firstName", "lastName", "email", "position", "team", "office"].map((key) => (
            <div className="filter-row" key={key}>
              <input
                type="text"
                value={searchMap[VISIBILITY_KEY].filter[key] || ""}
                placeholder={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")}
                onChange={(e) => setFilterValue(key, e.target.value)}
              />
              <button className="small btn-reset" onClick={() => setFilterValue(key, "")}>
                <i className="material-icons">close</i>
              </button>
            </div>
          ))}
        </div>
        <div className="sidebar-footer">
          <button onClick={() => setSearchField(VISIBILITY_KEY, "filter", {})}>
            <i className="material-icons">close</i>
            Reset
          </button>
        </div>
      </SidebarRight>
    </>
  );
}
